/**
 * Reservation Data Adapter
 * 将真实 API 响应转换为 Flyout 组件需要的格式
 */

import type { ApiReservation, FlyoutReservation, ReservationSource, ReservationStatusType, ReservationStatusValue } from '@/types/reservation';

/**
 * 将真实 API Reservation 转换为 Flyout Reservation
 * API 格式: GET /api/v1/pms/reservations/{id}?load_relationships=listing&load_relationships=guest
 */
export function adaptReservationForFlyout(apiReservation: any): FlyoutReservation {
  // 安全地获取值的辅助函数
  const safeString = (value: any, defaultValue: string = '') => {
    return value !== undefined && value !== null ? String(value) : defaultValue;
  };

  const safeNumber = (value: any, defaultValue: number = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  // 取 listing 第一张图的 URL（PMS 导入的统一 pictures 结构 { original, thumbnail, url }）
  const firstPictureUrl = (pictures: any): string => {
    if (Array.isArray(pictures) && pictures.length > 0) {
      const p = pictures[0];
      if (p && typeof p === 'object') return safeString(p.original || p.thumbnail || p.url);
      if (typeof p === 'string') return p;
    }
    return '';
  };

  // 映射 source
  // manual = 坐席 / PMS 后台手工建的单（业主自住也是 manual），与预订列表 OtaBadge 直接读 otaTypeCode 同口径，不再兜底成 Direct
  const sourceMapping: Record<string, ReservationSource> = {
    'airbnb': 'Airbnb',
    'vrbo': 'VRBO',
    'booking.com': 'Booking.com',
    'booking': 'Booking.com',
    'expedia': 'Expedia',
    'manual': 'Manual',
    'direct': 'Direct'
  };

  const sourceStr = safeString(apiReservation.otaTypeCode, 'direct').toLowerCase();
  const source = sourceMapping[sourceStr] || 'Direct';

  // 映射 status —— 覆盖 lookup.reservation_status_type 已知状态；
  // 未知状态原样透传，不再兜底成 Confirmed（2026-07-24 CS 反馈 inquiry 被当 Confirmed/Pending 展示）
  const statusMapping: Record<string, ReservationStatusType> = {
    'confirmed': 'Confirmed',
    'cancelled': 'Cancelled',
    'pending': 'Pending',
    'inquiry': 'Inquiry',
    'pending_confirmation': 'Pending Confirmation',
    'declined': 'Declined',
    'closed': 'Closed',
    'expired': 'Expired',
    'checked in': 'Checked In',
    'checked out': 'Checked Out',
    'checkedin': 'Checked In',
    'checkedout': 'Checked Out'
  };

  // 未知/缺失状态不再冒充 Confirmed:原码透传(缺失则空串),消费端用中性样式展示
  const rawStatus = safeString(apiReservation.reservationStatusCode);
  const status: ReservationStatusValue = statusMapping[rawStatus.toLowerCase()] || rawStatus;

  return {
    id: safeString(apiReservation.id),
    listingId: safeString(apiReservation.listingId),
    propertyCode: safeString(apiReservation.listing?.nickname || apiReservation.listing?.title || 'Unknown'),
    // 只放平台订单号，绝不回退内部 id——inquiry 没有 OTA code，回退会把
    // Snowflake id 冒充订单号展示（消费端自行决定空值兜底，如 '—'）
    reservationCode: safeString(apiReservation.otaConfirmationCode),
    startDate: safeString(apiReservation.checkIn),
    endDate: safeString(apiReservation.checkOut),
    source: source,
    guestName: safeString(apiReservation.guestFullName || apiReservation.guest?.fullName || 'Unknown'),
    nights: safeNumber(apiReservation.nightsCount, 1),
    status: status,
    guestCount: safeNumber(apiReservation.guestscount, 1),
    payout: safeNumber(apiReservation.netIncome, 0),
    email: safeString(apiReservation.guest?.email),
    phone: safeString(apiReservation.guest?.phone),
    // Financial details
    fareAccommodation: safeNumber(apiReservation.fareAccommodation, 0),
    totalFees: safeNumber(apiReservation.totalFees, 0),
    totalTaxes: safeNumber(apiReservation.totalTaxes, 0),
    // New fields
    keycode: safeString(apiReservation.keycode),
    confirmedAt: safeString(apiReservation.confirmedAt),
    canceledAt: safeString(apiReservation.canceledAt),
    createdAt: safeString(apiReservation.createdAt),
    listingNickname: safeString(apiReservation.listing?.nickname || apiReservation.listing?.title || ''),
    // listing 关系带出的 pictures（PMS 导入的统一 pictures 结构 { original, thumbnail, url }），取第一张作封面缩略图
    listingCoverUrl: firstPictureUrl(apiReservation.listing?.pictures),
    listingAddress: safeString(apiReservation.listing?.fullAddress),
    // Custom fields (mechanism A). Backend ReservationOutput.extra_info is an
    // array of {key,type,label,value,options?} (mirrors listing.extra_info).
    extraInfo: Array.isArray(apiReservation.extraInfo) ? apiReservation.extraInfo : [],
    pmsSource: safeString(apiReservation.pmsSource),
  };
}

/**
 * 批量转换
 */
export function adaptReservationsForFlyout(apiReservations: ApiReservation[]): FlyoutReservation[] {
  return apiReservations.map(adaptReservationForFlyout);
}
